import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  model,
  ModelSignal,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {MapService} from '../../services/impact-map/impact-map.service';
import {GlobalService} from '../../services/global/global.service';
import {GalleryModalComponent} from "../../modal/gallery-modal/gallery-modal.component";
import {ModalController, Platform} from "@ionic/angular";
import {GetMapApiService} from "../../services/z-map/get-map-api.service";
import {MapMarkerModel} from "../../models/map/map-marker/map-marker.model";
import {ApiResponse} from "../../services/utils";
import {CompanyService} from "../../services/company/company.service";
import {CompanyModel} from "../../models/company.model";
import {TeamService} from "../../services/team/team.service";
import {isPlatformBrowser} from "@angular/common";
import MapSheetModalComponent from "../../components/z-impact-map/map-sheet-modal/map-sheet-modal.component";
import {ActivatedRoute} from "@angular/router";
import {TeamModel} from "../../models/team.model";
import {firstValueFrom} from "rxjs";
import {environment} from "../../../environments/environment";
import {NavigationService} from "../../services/navigations/navigation.service";

interface Data {
  actionPointCount: number;
  impactScore: number;
  avgEmployeeImpactScore: number;
  employeeCount: number;
  adoptionCount: number;
  membersCount: number;
  averageImpactScore: number;
}

interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number];
  place_type?: string[];
}

interface MapGeocodeResponse {
  type: string;
  query: string[];
  features: MapboxFeature[];
  attribution: string;
}

type MapFilter = {
  type: "space" | "organization";
  id: number;
};

@Component({
  selector: 'app-impact-map',
  templateUrl: './impact-map.page.html',
  styleUrls: ['./impact-map.page.scss'],
  standalone: false
})
export class ImpactMapPage implements AfterViewInit, OnDestroy, OnInit {
  searchResultsOpen: ModelSignal<boolean> = model<boolean>(false);

  @ViewChild('mapEl', {static: false}) mapEl!: ElementRef<HTMLDivElement>;
  @ViewChild('mapLabel', {static: false}) mapLabel!: ElementRef<HTMLElement>;

  @Input() tabsVisible: boolean = false;
  @Input() panelWidth: number = 0;
  @Input() mapWidth: WritableSignal<number> = signal(0);

  public panelIsOpen: boolean = false;
  public legendIsOpen: boolean = false;
  public pushInfoVisible: boolean = false;
  public pushInfoState: 'show' | 'hiding' = 'show';
  public isBrowser: boolean = false;
  public isMobile: boolean = false;
  public isMobileBrowser: boolean = false;
  public pushInfoHideTimer?: number;

  public isModalOpen: boolean = false;
  public selectedMarker?: MapMarkerModel;

  private modalElement?: any;
  public company: CompanyModel

  public mapFilter: MapFilter | null = null;

  public activeSegment: string = 'posts';
  public location: { lng: number; lat: number }

  public data: Data = {
    actionPointCount: 0,
    impactScore: 0,
    avgEmployeeImpactScore: 0,
    employeeCount: 0,
    adoptionCount: 0,
    membersCount: 0,
    averageImpactScore: 0,
  };

  private map?: any;
  public searchResults: MapboxFeature[] = [];
  private searchAbort?: AbortController;

  public markers: MapMarkerModel[] = [];

  private deepLinkType?: string;
  private deepLinkId?: number;

  public hoverData: any = null;
  public hoverPos: { x: number; y: number } | null = null;
  private hoverSub?: any;
  public showSearchBar: boolean = false;

  // Injected services
  private mapService: MapService = inject(MapService);
  private globalService: GlobalService = inject(GlobalService);
  private navigationService: NavigationService = inject(NavigationService);
  private ngZone: NgZone = inject(NgZone);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private modalController: ModalController = inject(ModalController);
  private mapApi: GetMapApiService = inject(GetMapApiService);
  private companyService: CompanyService = inject(CompanyService);
  private teamService: TeamService = inject(TeamService);
  private platformId: Object = inject(PLATFORM_ID);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private MAPBOX_TOKEN: string = environment.mapboxAccessTokenWeb;

  private readonly ZOOM: number = 10;
  private readonly SPEED: number = 0.4;
  private readonly CURVE: number = 1.4;
  private readonly ESSENTIAL: boolean = true;


  constructor(
    private platform: Platform,
  ) {
    this.isBrowser = this.globalService.isBrowser;
    if (this.isBrowser) {
      this.isMobile = this.globalService.isMobile();
    } else {
      this.isMobile = true;
    }
  }

  public ngOnInit(): void {
    this.mapService.hover$.subscribe((d: MapMarkerModel): MapMarkerModel => this.hoverData = d);
    this.mapService.hoverPos$.subscribe((p: { x: number; y: number }): { x: number, y: number } => this.hoverPos = p);
    this.navigationService.resetMarker$.subscribe(() => {
      this.resetSelectedMarker();
    });

    if (isPlatformBrowser(this.platformId)) {
      this.loadAllMarkers();
    }

    this.isMobileBrowser = this.platform.is('mobileweb');
    this.isBrowser = this.platform.is('desktop');

    this.route.queryParams.subscribe(params => {
      this.deepLinkType = params['type'];
      this.deepLinkId = Number(params['id']);
    });

    this.mapService.markersUpdated$.subscribe(async () => {
      await this.reloadMapMarkers();
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    const params = this.route.snapshot.queryParams;

    const center = await this.mapService.resolveInitialLocationWithParams(params);
    this.map = this.mapService.initMap(this.mapEl.nativeElement, [center.lng, center.lat], 6);
    this.location = center;

    this.mapService.safeResizeSoon();

    await this.loadMarkers();

    if (!this.globalService.isWeb) {
      const mapLabel: HTMLElement = this.mapLabel?.nativeElement;
      const headerEl = document.querySelector('ion-header') as HTMLElement;
      if (mapLabel && headerEl) {
        mapLabel.style.position = 'absolute';
        mapLabel.style.top = `${headerEl.offsetHeight}px`;
      }
    }

    this.hoverSub = this.mapService.hover$.subscribe(async (marker: MapMarkerModel): Promise<void> => {
      if (!marker) {
        this.hoverData = null;
        this.hoverPos = null;
        this.cdr.markForCheck();
        return;
      }

      const updatePosition: () => void = (): void => {
        const pos = this.map.project([marker.longitude, marker.latitude]);
        const offsetX = 175;
        const offsetY = 260;
        this.hoverPos = {x: pos.x - offsetX, y: pos.y - offsetY};
        this.cdr.markForCheck();
      };

      updatePosition();
      this.map.on('move', updatePosition);

      this.map.on('move', () => {
        this.hoverData = null;
        this.hoverPos = null;
        this.cdr.markForCheck();
        this.mapService.clearAllHoverElements();
      });

      this.mapService.clearAllHoverElements();


      try {
        let data: ApiResponse;
        if (marker.type === 'organization') {
          data = await firstValueFrom(this.companyService.apiViewCompany(marker.id));
        } else if (marker.type === 'space') {
          data = await firstValueFrom(this.teamService.apiViewTeam(marker.id));
        } else {
          data = {data: marker} as any;
        }
        this.hoverData = data?.data ?? null;
      } catch (err) {
        console.error('Hover fetch error', err);
        this.hoverData = null;
      }

      this.cdr.markForCheck();
    });

    await this.loadMarkers();
    await this.checkDeepLink();

    this.mapService.flyToPosition$.subscribe(({lng, lat}) => {
      if (!this.map) return;

      if (this.isMobile) {
        const target = this.mapService.consumeLastFlyTarget(); // use new method
        if (target) {
          setTimeout(() => {
            this.map.flyTo({
              center: [target.lng, target.lat],
              zoom: 17,
              essential: true,
              speed: this.SPEED,
              curve: this.CURVE,
            });
          }, 600);
        }
      } else {
        this.map.flyTo({
          center: [lng, lat],
          zoom: 17,
          essential: true,
          speed: this.SPEED,
          curve: this.CURVE,
        });
      }

      this.map.once('moveend', () => {
        this.map.resize();
        this.map.triggerRepaint?.();
      });

      this.mapService.setActiveMarker?.(undefined);
    });

  }

  public ionViewWillEnter(): void {
    this.reloadMapMarkers();

    this.route.queryParams.subscribe(params => {
      this.deepLinkType = params['type'];
      this.deepLinkId = Number(params['id']);

      this.checkDeepLink();
    });
  }

  public async ionViewDidEnter(): Promise<void> {
    this.mapService.safeResizeSoon();

    const mapLabel = this.mapLabel?.nativeElement;
    const headerEl = document.querySelector('ion-header') as HTMLElement;

    if (mapLabel && headerEl) {
      mapLabel.style.position = 'absolute';
      mapLabel.style.top = `${headerEl.offsetHeight}px`;
    }

    await this.loadMarkers();
    await this.checkDeepLink();
  }

  public ionViewDidLeave(): void {
    this.panelIsOpen = false;
    this.legendIsOpen = false;
  }

  public ngOnDestroy(): void {
    this.cancelInFlightSearch();
    this.mapService.teardown(this.mapEl);
    this.hoverSub?.unsubscribe();
    if (this.map) this.map.off('move');
  }

  public closePushInfo(): void {
    if (this.pushInfoState === 'hiding') return;
    this.closePanel();
    if(!this.isMobile){
      this.navigationService.openHome();
    }
  }

  public toggleMapLegend(): void {
    this.legendIsOpen = !this.legendIsOpen;
    this.mapService.safeResizeSoon();
  }

  /** =======================
   *   Address search
   *  ======================= */
  public trackSearch: (_: number, item: MapboxFeature) => string = (_: number, item: MapboxFeature): string => item.id;

  public async onSearchChange(ev: CustomEvent): Promise<void> {
    if (!this.globalService.isBrowser) return;

    const q = (ev as any)?.detail?.value?.trim?.() ?? '';
    if (!q) {
      this.searchResults = [];
      this.cancelInFlightSearch();
      return;
    }

    this.cancelInFlightSearch();
    this.searchAbort = new AbortController();

    try {
      const center = this.getMapCenter();
      const url = new URL(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
      );
      url.searchParams.set('access_token', this.MAPBOX_TOKEN);
      url.searchParams.set('autocomplete', 'true');
      url.searchParams.set('limit', '8');
      url.searchParams.set('language', 'en');
      if (center) url.searchParams.set('proximity', `${center[0]},${center[1]}`);

      const res = await fetch(url.toString(), {
        signal: this.searchAbort.signal,
        headers: {Accept: 'application/json'},
      });
      if (!res.ok) {
        console.error('Geocoding failed', res.status);
        this.searchResults = [];
        return;
      }
      const data = (await res.json()) as MapGeocodeResponse;
      this.searchResults = data.features ?? [];
      this.searchResultsOpen.set(this.searchResults.length > 1
        && ((this.tabsVisible && this.mapWidth() < 900) || (!this.tabsVisible && this.mapWidth() < 300)));
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        console.error('Mapbox geocoding error:', e);
        this.searchResults = [];
      }
    }
  }

  public clearSearch(): void {
    this.searchResults = [];
    this.cancelInFlightSearch();
  }

  public selectSearchResult(f: MapboxFeature): void {
    this.searchResults = [];
    if (!this.map || !this.globalService.isBrowser) return;

    try {
      if ((f as any).bbox && Array.isArray((f as any).bbox) && (f as any).bbox.length === 4) {
        const [minLng, minLat, maxLng, maxLat] = (f as any).bbox;

        this.map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat]
          ],
          {
            padding: 40,
            speed: this.SPEED,
            curve: this.CURVE,
            essential: this.ESSENTIAL,
            pitch: 60,
            bearing: 0,
          }
        );


        this.map.once('moveend', () => {
          this.map.easeTo({
            pitch: 60,
            bearing: 0,
            duration: 800
          });
        });
      } else {
        const [lng, lat] = f.center;
        this.map.flyTo({
          center: [lng, lat],
          zoom: 12.5,
          speed: this.SPEED,
          curve: this.CURVE,
          essential: this.ESSENTIAL,
        });
      }
    } catch (err) {
      console.error('FlyTo/fitBounds failed, fallback to mapService', err);
      if ((this.mapService as any).flyTo) {
        const [lng, lat] = f.center;
        (this.mapService as any).flyTo([lng, lat], 14);
      }
    }
  }

  public async openModal(point: MapMarkerModel): Promise<void> {
    this.selectedMarker = point;

    const modal: HTMLIonModalElement = await this.modalController.create({
      component: MapSheetModalComponent,
      mode: "ios",
      breakpoints: [0, 0.5, 1],
      initialBreakpoint: 0.5,
      handleBehavior: 'none',
      componentProps: {marker: this.selectedMarker}
    });

    modal.onWillDismiss().then(() => {
      if(!this.isMobile){
        this.cleanupAfterModal();
      }
    });

    await modal.present();
  }

  public async closeModal(): Promise<void> {
    this.isModalOpen = false;
    this.selectedMarker = undefined;
    this.selectedMarker = undefined;
    this.hoverData = null;
    this.hoverPos = null;
    this.mapService.clearActiveMarker();
    this.mapService.clearAllHoverElements();
    this.cdr.markForCheck();
  }

  public async closePanel(): Promise<void> {
    this.legendIsOpen = false;
    this.panelIsOpen = false;
    this.pushInfoVisible = false;
    this.deepLinkType = null;
    this.deepLinkId = null;
    this.mapFilter = null;

    this.mapService.clearAllHoverElements();
    this.mapService.clearActiveMarker();

    await this.reloadMapMarkers();
  }

  public isCompanyModel(data: any): data is CompanyModel {
    return !!data && 'employeeCount' in data;
  }

  public isTeamModel(data: any): data is TeamModel {
    return !!data && 'membersCount' in data;
  }

  public flyToPosition(): void {
    if (this.map) {
      this.map.flyTo({
        center: this.location,
        zoom: this.ZOOM,
        speed: this.SPEED,
        curve: this.CURVE,
        essential: this.ESSENTIAL
      });
    }
  }

  public toHome(): void {
    this.navigationService.openHome();
  }

  private setActiveFilter(marker: MapMarkerModel): void {
    this.pushInfoVisible = true;
    this.pushInfoState = 'show';

    this.selectedMarker = marker;

    let selectedType: "space" | "organization";
    if (marker.type === "space") {
      this.mapApi.getMapMarkers('space', this.selectedMarker.id).subscribe({
        next: (markers: MapMarkerModel[]): void => {
          this.markers = markers;

          this.mapService.addMarkers(this.markers, (p: MapMarkerModel): void => this.onMarkerClick(p));
          this.mapService.setActiveMarker(this.selectedMarker.id);
        },
        error: (err: any): void => console.error('Error loading organization markers:', err)
      });
      selectedType = "space";
    } else {
      this.mapApi.getMapMarkers('organization', this.selectedMarker.id).subscribe({
        next: (markers: MapMarkerModel[]): void => {
          this.markers = markers;
          this.mapService.addMarkers(this.markers, (p: MapMarkerModel): void => this.onMarkerClick(p));
          this.mapService.setActiveMarker(this.selectedMarker.id);
        },
        error: (err) => console.error('Error loading organization markers:', err)
      });
      selectedType = "organization";
    }

    this.mapFilter = {
      type: selectedType,
      id: marker.id
    };
  }

  private cancelInFlightSearch(): void {
    try {
      this.searchAbort?.abort();
    } catch {
    }
    this.searchAbort = undefined;
    this.searchResultsOpen.set(false);
  }

  private loadAllMarkers(): void {
    this.mapApi.getMapMarkers().subscribe({
      next: (markers: MapMarkerModel[]): void => {
        this.markers = markers;
        this.mapService.addMarkers(this.markers, (p) => this.onMarkerClick(p));
        this.checkDeepLink();
      },
      error: (err: any): void => console.error('API error:', err)
    });
  }

  private onMarkerClick(p: MapMarkerModel) {
    this.mapService.clearAllHoverElements();

    if (!this.globalService.sidePanelOpen()) {
      this.globalService.sidePanelOpen.set(true);
    }

    this.ngZone.run((): void => {
      this.selectedMarker = p;
      this.mapService.setActiveMarker(p.id);

      // --- Case 1: Mobile Web ---
      if (this.isMobile) {
        if (p.type === 'moment') {
          this.openMomentGallery(p.id, true);
        } else {
          this.openModal(p);
          if (p.type === 'organization' || p.type === 'space'){
            this.setActiveFilter(p);
          }
        }

      } else {
        this.handleWebNavigation(p);
      }

      this.cdr.markForCheck();
    });
  }

  private async openMomentGallery(id: number, actionPointFeed: boolean): Promise<void> {
    this.modalElement = await this.modalController.create({
      component: GalleryModalComponent,
      backdropDismiss: true,
      mode: 'ios',
      id: 'inline-modal',
      cssClass: 'modal-gallery',
      componentProps: {momentId: id, actionPointId: null, actionPointFeed, singleMode: true}
    });

    this.modalElement.onWillDismiss().then((): void => {
      this.cleanupAfterModal();
    });

    await this.modalElement.present();
  }


  private async checkDeepLink(): Promise<void> {
    let deepLinkType: 'space' | 'organization' | 'moment' |null = null;
    let maybeId: number = 0;
    if(this.deepLinkType === 'moment' && this.deepLinkId){
      deepLinkType = this.deepLinkType;
      maybeId = this.deepLinkId;
    }else {
      const url = new URL(window.location.href);
      const pathParts: string[] = url.pathname.split('/').filter(Boolean);

      const lastPart: string = pathParts[pathParts.length - 1];
      maybeId = Number(lastPart);

      const isSpace: boolean = pathParts.includes('space') || pathParts.includes('team');
      const isCompany: boolean = pathParts.includes('company');
      const filterTrue: boolean = url.searchParams.get('filter') === 'true';

      if (!maybeId || !filterTrue) return;

      if (isCompany) {
        deepLinkType = 'organization';
      } else if (isSpace) {
        deepLinkType = 'space';
      }
      if (!deepLinkType) return;
    }

    let tries: number = 0;
    while ((!this.map || !this.markers.length) && tries < 20) {
      await new Promise((r: (value: (PromiseLike<unknown> | unknown)) => void) => setTimeout(r, 150));
      tries++;
    }
    if (!this.map || !this.markers.length) return;

    const target: MapMarkerModel = this.markers.find(
      (m: MapMarkerModel): boolean => m.id === maybeId && m.type === deepLinkType
    );

    if (!target) {
      console.warn('No marker found for URL', { deepLinkType, maybeId });
      return;
    }

    if(deepLinkType === 'moment'){
      this.location = {lng: target.longitude, lat: target.latitude}
      this.flyToPosition();
    } else {
      this.setActiveFilter(target);
      this.selectedMarker = target;
      this.pushInfoVisible = true;
    }
  }

  private async loadMarkers(): Promise<void> {
    const fetchPoints: () => Promise<MapMarkerModel[]> = async (): Promise<MapMarkerModel[]> => this.markers;
    await this.mapService.loadAndAddMarkers(fetchPoints, (p: MapMarkerModel) => this.onMarkerClick(p));

    if (this.location) {
      this.map.flyTo({
        center: [this.location.lng, this.location.lat],
        zoom: 12,
        speed: this.SPEED,
        curve: this.CURVE,
        essential: this.ESSENTIAL
      });
    } else {
      this.flyToPosition();
    }
  }


  private getMapCenter(): [number, number] | undefined {
    try {
      if (this.map && typeof this.map.getCenter === 'function') {
        const c = this.map.getCenter();
        if (c && typeof c.lng === 'number' && typeof c.lat === 'number') {
          return [c.lng, c.lat];
        }
      }
    } catch {
    }
    return undefined;
  }

  private async reloadMapMarkers(): Promise<void> {
    try {
      this.markers = await firstValueFrom(this.mapApi.getMapMarkers());

      await this.mapService.reloadMarkers();
    } catch (err) {
      console.error('Error reloading markers:', err);
    }
  }

  private async handleWebNavigation(p: MapMarkerModel): Promise<void> {
    if (p.type === 'space') {
      this.setActiveFilter(p);
      await this.navigationService.openTeamProfile({id: p.id, isAdmin: false});
      return;
    }

    if (p.type === 'organization') {
      this.setActiveFilter(p);
      try {
        const result = await firstValueFrom(this.companyService.apiViewCompany(p.id));
        if (!result?.status || !result.data) {
          console.error('Failed to load company:', result);
          return;
        }
        const company = result.data as CompanyModel;
        await this.navigationService.openCompanyProfileById(company.id, company.isAdmin);
      } catch (err) {
        console.error('Error loading company', err);
      }
      return;
    }

    if (p.type === 'moment') {
      await this.navigationService.openMapMoment(p.id);
      return;
    }

    await this.navigationService.openPointOfActionDetails(p.id);
  }

  private resetSelectedMarker(): void {
    if (this.selectedMarker && (this.selectedMarker.type === 'moment' || this.selectedMarker.type === 'poa')) {
      this.selectedMarker = undefined;
      this.hoverData = null;
      this.hoverPos = null;
      this.mapService.clearActiveMarker();
      this.mapService.clearAllHoverElements();
      this.cdr.markForCheck();
    }
  }

  private cleanupAfterModal(): void {
    this.selectedMarker = undefined;
    this.hoverData = null;
    this.hoverPos = null;
    this.mapService.clearActiveMarker();
    this.mapService.clearAllHoverElements();
    this.cdr.markForCheck();
  }
}
